import { useState, useRef, useEffect, type JSX } from "react";
import styles from "./DocumentsTable.module.css";

type DocumentType = {
  id: string;
  properties: (string | number | JSX.Element)[];
};

type DocumentsTableProps = {
  titles: string[];
  documents: DocumentType[] | undefined;
  selectedIds?: string[];
  handleCheckboxChange?: (id: string) => void;
  handleCheckboxSelectAll?: () => void;
};

const DocumentsTable = ({
  titles,
  documents,
  selectedIds,
  handleCheckboxChange,
  handleCheckboxSelectAll,
}: DocumentsTableProps) => {
  const [expandedColumns, setExpandedColumns] = useState<number[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleDoubleClick = (index: number) => {
    setExpandedColumns((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      if (e.deltaY !== 0) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  if (!documents || documents.length === 0) {
    return <></>;
  }

  const isTableWithCheckboxes = selectedIds && selectedIds !== null;

  const allSelected =
    isTableWithCheckboxes &&
    documents.length > 0 &&
    documents.every((doc) => selectedIds.includes(doc.id));

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableScrollWrapper} ref={scrollRef}>
        <table className={styles.table}>
          <thead>
            <tr>
              {isTableWithCheckboxes && (
                <th className={styles.tableCheckBox}>
                  <input
                    className={styles.customCheckbox}
                    type="checkbox"
                    onChange={handleCheckboxSelectAll}
                    checked={allSelected}
                  />
                </th>
              )}
              {titles.map((title, index) => (
                <th
                  key={index}
                  onDoubleClick={() => handleDoubleClick(index)}
                  className={
                    index === 1 ? styles.tableTitleCentered : styles.tableTitle
                  }
                >
                  {title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {documents.map((doc) => (
              <tr key={doc.id}>
                {isTableWithCheckboxes && (
                  <td className={styles.tableCheckBox}>
                    <input
                      className={styles.customCheckbox}
                      type="checkbox"
                      checked={selectedIds && selectedIds.includes(doc.id)}
                      onChange={() =>
                        handleCheckboxChange && handleCheckboxChange(doc.id)
                      }
                    />
                  </td>
                )}
                {doc.properties.map((cell, index) => (
                  <td
                    key={index}
                    title={typeof cell === "string" ? cell : undefined}
                    className={`${
                      index === 1 ? styles.tableDataCentered : styles.tableData
                    } ${
                      expandedColumns.includes(index) ? styles.fullWidth : ""
                    }`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DocumentsTable;
